import type { AnalyticsProps } from "./AnalyticsAdapter";

export const HEARTBEAT_MS = 60_000;

export interface SessionTrackerDeps {
  track: (event: string, props?: AnalyticsProps) => void;
  now: () => number;
  sessionId: string;
}

export interface SessionTracker {
  start(): void;
  heartbeat(visible: boolean): void;
  noteContractCompleted(): void;
  noteScreen(screen: string): void;
  end(): void;
}

/**
 * Session lifecycle events from the tracking plan (§session_*): one
 * `session_started` on boot, a `session_heartbeat` per minute while the tab is
 * visible, and a single `session_ended` with duration, contracts completed and
 * distinct screens visited. Pure: time and transport are injected.
 */
export function createSessionTracker(deps: SessionTrackerDeps): SessionTracker {
  let startedAt = 0;
  let ended = false;
  let contractsCompleted = 0;
  const screens = new Set<string>();
  const base = { sessionId: deps.sessionId };

  return {
    start(): void {
      startedAt = deps.now();
      deps.track("session_started", { ...base });
    },
    heartbeat(visible): void {
      if (!visible || ended) {
        return;
      }
      deps.track("session_heartbeat", {
        ...base,
        secondsActive: Math.round((deps.now() - startedAt) / 1000),
      });
    },
    noteContractCompleted(): void {
      contractsCompleted += 1;
    },
    noteScreen(screen): void {
      screens.add(screen);
    },
    end(): void {
      if (ended) {
        return;
      }
      ended = true;
      deps.track("session_ended", {
        ...base,
        durationSec: Math.round((deps.now() - startedAt) / 1000),
        contractsCompleted,
        screensVisited: screens.size,
      });
    },
  };
}

let current: SessionTracker | null = null;

export function setSessionTracker(tracker: SessionTracker | null): void {
  current = tracker;
}

export function getSessionTracker(): SessionTracker | null {
  return current;
}
